import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";

const isInFlow = (element) => {
  if (!element.getClientRects().length) return false;

  const { position } = window.getComputedStyle(element);
  return position !== "absolute" && position !== "fixed";
};

const marginBottom = (element) =>
  parseFloat(window.getComputedStyle(element).marginBottom) || 0;

// How far the page extends below the footer because of what is stacked under
// it: each ancestor's bottom margin, padding and border, plus any in-flow
// sibling that sits below. Measuring to the body's bottom instead would also
// count the filler of a taller neighbouring column (e.g. a full-height
// sidebar), which grows as the table shrinks and drags it to its minimum.
const measureBelowFooter = (footer) => {
  const footerBottom = footer.getBoundingClientRect().bottom;
  let edge = footerBottom;
  let node = footer;

  while (node !== document.body && node.parentElement) {
    const parent = node.parentElement;
    edge += marginBottom(node);

    for (const child of parent.children) {
      if (child === node || !isInFlow(child)) continue;

      const rect = child.getBoundingClientRect();
      // Beside or above the footer's column, so it doesn't push the page down.
      if (rect.top < edge - 1) continue;

      edge = Math.max(edge, rect.bottom + marginBottom(child));
    }

    const style = window.getComputedStyle(parent);
    edge +=
      (parseFloat(style.paddingBottom) || 0) +
      (parseFloat(style.borderBottomWidth) || 0);

    node = parent;
  }

  return Math.max(0, edge - footerBottom);
};

function useAutoAdjustTableHeight(bottomRef, disableAutoResizing) {
  const [tableHeight, setTableHeight] = useState(0);
  const tableRef = useRef(null);
  const toolbarRef = useRef(null);
  const timeoutRef = useRef(null);
  const recentHeightsRef = useRef([]);

  const calculateHeight = useCallback(() => {
    if (disableAutoResizing || !tableRef.current) return;

    const viewportHeight =
      document.documentElement.clientHeight || window.innerHeight;
    const offsetTop = tableRef.current.getBoundingClientRect().top || 0;
    const bottomRect = bottomRef.current?.getBoundingClientRect();
    const bottomOffset = bottomRect?.height || 0;

    // Ensure we have valid measurements
    if (offsetTop <= 0) return;

    // Whatever the page puts below the footer — wrapper padding, a trailing
    // section — has to come out of the table's budget, or the document ends
    // past the viewport and raises a scrollbar.
    const belowFooter = bottomRef.current
      ? measureBelowFooter(bottomRef.current)
      : 0;

    let availableHeight =
      viewportHeight - offsetTop - bottomOffset - belowFooter;

    // Set minimum and maximum bounds
    const minHeight = 200;

    // Only cap on smaller screens
    const maxHeight =
      viewportHeight < 900 ? viewportHeight * 0.9 : viewportHeight;

    availableHeight = Math.floor(
      Math.max(minHeight, Math.min(maxHeight, availableHeight)),
    );

    setTableHeight((prevHeight) => {
      // Shrinking is what keeps the footer inside the viewport, so it is never
      // deferred. Only growth is damped.
      if (availableHeight < prevHeight) {
        return availableHeight;
      }

      if (availableHeight - prevHeight <= 10) {
        return prevHeight;
      }

      // A toolbar wrap can toggle the page scrollbar, which changes the
      // viewport width, which unwraps the toolbar again — bouncing back to a
      // height we just left. Refuse that bounce so the two states don't
      // fight forever.
      const isBounceBack = recentHeightsRef.current.some(
        (h) => Math.abs(h - availableHeight) <= 10,
      );
      if (isBounceBack) {
        return prevHeight;
      }

      return availableHeight;
    });
  }, [bottomRef, disableAutoResizing]);

  useEffect(() => {
    const history = recentHeightsRef.current;
    const previous = history[history.length - 1];

    // A shrink is forced by the geometry rather than a bounce, so it must not
    // sit in the history and veto the matching growth when the layout returns.
    if (previous !== undefined && tableHeight < previous) {
      recentHeightsRef.current = [tableHeight];
      return;
    }

    history.push(tableHeight);
    if (history.length > 3) history.shift();
  }, [tableHeight]);

  useLayoutEffect(() => {
    calculateHeight();
  }, [calculateHeight]);

  useEffect(() => {
    if (disableAutoResizing) return undefined;

    const handleResize = () => {
      // Debounce resize calculations
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(calculateHeight, 150);
    };

    window.addEventListener("resize", handleResize);

    // Observe both the table and bottom element for changes
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(calculateHeight, 50);
    });

    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }

    if (bottomRef.current) {
      resizeObserver.observe(bottomRef.current);
    }

    // Observe the toolbar (search/filters row) so wrapping there is picked up,
    // without reacting to unrelated layout changes elsewhere on the page.
    if (toolbarRef.current) {
      resizeObserver.observe(toolbarRef.current);
    }

    return () => {
      clearTimeout(timeoutRef.current);
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [calculateHeight, bottomRef, disableAutoResizing]);

  return { tableHeight, tableRef, toolbarRef };
}

export default useAutoAdjustTableHeight;
